import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { SwallowMark } from './SwallowMark';

/**
 * The tinycms bird in flight: what the app shows wherever something is
 * loading. The drawing is the designer's bird-loader.svg
 * (public/brand/tinycms): the full brush bird whose wing morphs through a real
 * flap, animated inside the file (SMIL, no script). It is fetched once and
 * inlined so it takes the text colour around it; the file swaps itself for
 * the still bird, fading, for people who ask for less motion. Styles in
 * src/styles/ink.css, .swallow-loader. It fades in after a beat so a quick
 * load never flashes it.
 *
 *   PageLoader  a whole screen (variant 'screen') or the space under the top bar
 *   Loader      in a panel, list or card (size 'block'), or beside text in a
 *               button or status line (size 'inline')
 *
 * The flying bird is drawn for 64px and up, so the inline size shows the
 * still mark breathing instead (the same path as SwallowMark).
 *
 * The label keeps each place's own words ("Loading projects"); "…" is added.
 *
 * When nothing else is on the page below it (and it isn't in a dialog), a
 * loader grows so the bird flies at the middle of the screen rather than just
 * under whatever is above it (useScreenCentre).
 */

const LOADER_URL = `${process.env.PUBLIC_URL || ''}/brand/tinycms/bird-loader.svg`;

let loaderSvg: string | null = null;
let loaderFetch: Promise<string | null> | null = null;

/** The loader file's markup, fetched once per page and shared by every loader. */
export function fetchLoaderSvg(): Promise<string | null> {
  if (loaderSvg) return Promise.resolve(loaderSvg);
  if (!loaderFetch) {
    loaderFetch =
      typeof fetch === 'function'
        ? fetch(LOADER_URL)
            .then((r) => (r.ok ? r.text() : null))
            .then((text) => {
              if (!text || !text.includes('<svg')) return null;
              // Our box sizes it and names it; drop the file's own size and label.
              loaderSvg = text.replace(/<svg[^>]*>/, (tag) =>
                tag
                  .replace(/\s(width|height|role|aria-label)="[^"]*"/g, '')
                  .replace('<svg', '<svg aria-hidden="true"')
              );
              return loaderSvg;
            })
            .catch(() => null)
        : Promise.resolve(null);
    // A failed download isn't kept: the next loader tries again.
    loaderFetch.then((text) => {
      if (!text) loaderFetch = null;
    });
  }
  return loaderFetch;
}
// Start the download when the app loads, so the first loader has it.
fetchLoaderSvg();

export function SwallowFlight({ className }: { className?: string }) {
  const [svg, setSvg] = useState<string | null>(loaderSvg);
  useEffect(() => {
    if (svg) return undefined;
    let live = true;
    fetchLoaderSvg().then((text) => {
      if (live && text) setSvg(text);
    });
    return () => {
      live = false;
    };
  }, [svg]);
  const cls = `swallow-loader-bird ${className || ''}`.trim();
  // Until the file arrives: the still mark, so the box has its shape.
  if (!svg) return <SwallowMark className={cls} />;
  return <span className={cls} dangerouslySetInnerHTML={{ __html: svg }} />;
}

/** Laid out in normal flow and taking up room: content a reader would see below. */
function isVisibleContent(el: Element): boolean {
  const style = window.getComputedStyle(el);
  if (style.position === 'fixed' || style.position === 'absolute' || style.display === 'none') {
    return false;
  }
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** True when anything visible comes after `el` on the page (or it sits in a dialog). */
export function hasContentAfter(el: Element): boolean {
  if (el.closest('[role="dialog"], [aria-modal="true"]')) return true;
  for (let node: Element | null = el; node && node !== document.body; node = node.parentElement) {
    for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
      if (isVisibleContent(sib)) return true;
    }
  }
  return false;
}

/**
 * The height that puts this box's middle at the middle of the screen: from
 * its top down to as far below the screen's centre as its top is above it.
 * Null when it already starts past the middle, or something follows it.
 */
export function centringHeight(top: number, viewport: number): number | null {
  const h = viewport - 2 * top;
  return h > 0 ? h : null;
}

/** Grow `ref` so its contents sit at the screen's vertical centre, when the page below is empty. */
function useScreenCentre<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const measure = () => {
      if (hasContentAfter(el)) return setMinHeight(null);
      // Measured without the height this hook added, so it never feeds on itself.
      const top = el.getBoundingClientRect().top;
      setMinHeight(centringHeight(top, window.innerHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled]);
  return { ref, minHeight };
}

const withEllipsis = (label: string) => `${label.replace(/(…|\.\.\.)\s*$/, '')}…`;

export function PageLoader({
  label = 'Loading',
  variant = 'section',
}: {
  label?: string;
  variant?: 'screen' | 'section';
}) {
  const { ref, minHeight } = useScreenCentre<HTMLDivElement>(variant === 'section');
  return (
    <div
      ref={ref}
      className="swallow-loader"
      data-variant={variant}
      role="status"
      aria-live="polite"
      style={minHeight ? { minHeight } : undefined}
    >
      <SwallowFlight />
      <span className="swallow-loader-label">{withEllipsis(label)}</span>
    </div>
  );
}

export function Loader({
  label = 'Loading',
  size = 'block',
  className,
  style,
}: {
  label?: string;
  size?: 'block' | 'inline';
  className?: string;
  style?: CSSProperties;
}) {
  const { ref, minHeight } = useScreenCentre<HTMLDivElement>(size === 'block');
  const body = (
    <>
      {size === 'inline' ? <SwallowMark className="swallow-loader-bird" /> : <SwallowFlight />}
      <span className="swallow-loader-label">{withEllipsis(label)}</span>
    </>
  );
  const common = {
    className: `swallow-loader${className ? ` ${className}` : ''}`,
    'data-variant': size,
    role: 'status',
    'aria-live': 'polite' as const,
  };
  if (size === 'inline') {
    return (
      <span {...common} style={style}>
        {body}
      </span>
    );
  }
  return (
    <div {...common} ref={ref} style={minHeight ? { ...style, minHeight } : style}>
      {body}
    </div>
  );
}
